import * as React from 'react';
import { useEffect } from "react";
import { useState } from "react";
import {
    Box,
    SiteVariablesPrepared,
    Toolbar as Legend,
    Button as LegendItem,
    BoldIcon,
    Flex,
} from "@fluentui/react-northstar";
import { IChartPatterns, ILegendItem } from "../chart-types";
import { legendLabels } from "../chart-patterns";
import { getText } from "../../../translations";

const LabelColorValue = ({
    index,
    siteVariables,
    dataPointColor,
    patterns,
    activeSegmentIndex,
}: {
    index: number;
    siteVariables: SiteVariablesPrepared;
    dataPointColor: string;
    patterns?: IChartPatterns;
    activeSegmentIndex?: number[] | number;
}) => {
    const { borderRadius, theme, colorScheme, colors } = siteVariables;
    const labelColorValueRef = React.useRef<HTMLCanvasElement | null>(null);
    useEffect(() => {
        if (!labelColorValueRef.current) return;
        const canvasRef: HTMLCanvasElement = labelColorValueRef.current;
        legendLabels({
            index,
            canvasRef,
            colorScheme,
            patterns,
            dataPointColor,
            activeSegmentIndex,
        });
    }, [theme, activeSegmentIndex]);
    return (
        <Box
            styles={{
                width: ".75rem",
                minWidth: ".75rem",
                height: ".75rem",
                minHeight: ".75rem",
                marginBottom: "-1px",
                marginRight: ".4rem",
            }}
        >
            <canvas
                ref={labelColorValueRef}
                tabIndex={0}
                style={{
                    width: "100%",
                    height: "100%",
                    userSelect: "none",
                    border: "none",
                    borderRadius: borderRadius,
                }}
            />
        </Box>
    );
};

const legendItem = ({
    key,
    value,
    hidden,
    siteVariables,
    chartDataPointColors,
    onLegendClick,
    patterns,
    activeSegmentIndex,
}: {
    key: number;
    value: string;
    siteVariables: SiteVariablesPrepared;
    chartDataPointColors: any;
    onLegendClick: (key: number) => void;
    hidden?: boolean;
    patterns?: IChartPatterns;
    activeSegmentIndex?: number[] | number;
}): ILegendItem => {
    const { t } = siteVariables;

    let legendColor = siteVariables.colorScheme.default.foreground2

    if (activeSegmentIndex != null && ((activeSegmentIndex[0] != null && activeSegmentIndex[1] != null))) {
        if (activeSegmentIndex[1] === (key)) {
            legendColor = siteVariables.colorScheme.default.foreground2;
        } else {
            legendColor = "#f5f5f5";
        }
    } else {
        legendColor = siteVariables.colorScheme.default.foreground2;
    }

    return {
        key,
        kind: "custom",
        onClick: () => {
            //onLegendClick(key);
        },
        content: (
            <LegendItem
                styles={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: ".75rem",
                    minWidth: "30px",
                    color: legendColor,
                    margin: "2px 0",
                }}
                text
            >
                <LabelColorValue
                    index={key}
                    siteVariables={siteVariables}
                    dataPointColor={chartDataPointColors[key]}
                    patterns={patterns}
                    activeSegmentIndex={activeSegmentIndex}
                />
                {getText(t?.locale, value)}
            </LegendItem>
        ),
        fitted: "horizontally",
    };
};

const LegendItems = (
    data: any,
    siteVariables: SiteVariablesPrepared,
    chartDataPointColors: any,
    onLegendClick: (index: number) => void,
    verticalDataAlignment?: boolean,
    patterns?: IChartPatterns,
    activeSegmentIndex?: number[] | number,
): ILegendItem[] =>
    verticalDataAlignment
        ? Array.from(data.labels, (label: any, key) =>
            legendItem({
                key,
                value: label,
                siteVariables,
                chartDataPointColors,
                onLegendClick,
                patterns,
                activeSegmentIndex,
            })
        )
        : Array.from(data.datasets, (dataset: any, key) =>
            legendItem({
                key,
                value: dataset.label,
                hidden: dataset.hidden,
                siteVariables,
                chartDataPointColors,
                onLegendClick,
                patterns,
                activeSegmentIndex,
            })
        );

export const ChartContainer = ({
    data,
    children,
    siteVariables,
    chartDataPointColors,
    onLegendClick,
    verticalDataAlignment,
    patterns,
    hideLegend,
    customWidth,
    customHeight,
    customLegend,
    activeSegmentIndex,
}: {
    data: any;
    children: React.ReactNode;
    siteVariables: SiteVariablesPrepared;
    chartDataPointColors: any;
    onLegendClick: (index: number) => void;
    verticalDataAlignment?: boolean;
    patterns?: IChartPatterns;
    hideLegend?: boolean;
    customWidth?: string;
    customHeight?: string;
    customLegend?: boolean;
    activeSegmentIndex?: number[] | number;
}) => {
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [overflowItems, setOverflowItems] = useState<number>(0);
    const { theme, colorScheme, t } = siteVariables;
    let legendItems: ILegendItem[] = LegendItems(
        data,
        siteVariables,
        chartDataPointColors,
        onLegendClick,
        verticalDataAlignment,
        patterns,
        activeSegmentIndex
    );

    useEffect(() => {
        legendItems = LegendItems(
            data,
            siteVariables,
            chartDataPointColors,
            onLegendClick,
            verticalDataAlignment,
            patterns,
            activeSegmentIndex
        );
    }, [theme, t]);

    return (
        <Flex
            column
            style={{
                height: customHeight ? customHeight : "100%",
                margin: "0 -1rem 0 0",
                paddingBottom: ".5rem",
                width: customWidth ? customWidth : "100%",
                zIndex: 100
            }}
        >
            {customLegend &&
                <Box className="legendBox" styles={{ display: hideLegend ? 'none' : 'block' }}>
                    <Legend
                        className="legendWrapper"
                        aria-label={"toolbar overflow menu"}
                        items={legendItems as any}
                        overflow
                        overflowOpen={overflowOpen}
                        overflowItem={{
                            icon: (
                                <BoldIcon
                                    styles={{
                                        position: "relative",
                                        width: "3.5rem",
                                        height: "1rem",
                                        borderRadius: "4px",
                                        "& svg": {
                                            display: "none",
                                        },
                                        "&::after": {
                                            content: `"${overflowItems} more"`,
                                            display: "block",
                                            position: "absolute",
                                            top: 0,
                                            left: ".5rem",
                                            minWidth: "3rem",
                                            textAlign: "left",
                                            fontSize: ".75rem",
                                            color: colorScheme.brand.foreground,
                                        },
                                    }}
                                />
                            ),
                        }}
                        onOverflowOpenChange={(e, props) => {
                            setOverflowOpen(!!props?.overflowOpen);
                        }}
                        onOverflow={(items) => setOverflowItems(legendItems.length - items)}
                        getOverflowItems={(startIndex) => legendItems.slice(startIndex)}
                        styles={{
                            width: "100%",
                            backgroundColor: colorScheme.grey.background,
                        }}
                    />
                </Box>
            }
            
            <Box
                styles={{
                    flexGrow: 1,
                    backgroundColor: "transparent",
                }}
            >
                {children}
            </Box>

            {/* Legend should be in differen container to avoid FluentUI window resize issue */}
            {!customLegend && 
                <Box className="legendBox" styles={{ display: hideLegend ? 'none' : 'block' }}>
                    <Legend
                        className="legendWrapper"
                        aria-label={"toolbar overflow menu"}
                        items={legendItems as any}
                        overflow
                        overflowOpen={overflowOpen}
                        overflowItem={{
                            icon: (
                                <BoldIcon
                                    styles={{
                                        position: "relative",
                                        width: "3.5rem",
                                        height: "1rem",
                                        borderRadius: "4px",
                                        "& svg": {
                                            display: "none",
                                        },
                                        "&::after": {
                                            content: `"${overflowItems} more"`,
                                            display: "block",
                                            position: "absolute",
                                            top: 0,
                                            left: ".5rem",
                                            minWidth: "3rem",
                                            textAlign: "left",
                                            fontSize: ".75rem",
                                            color: colorScheme.brand.foreground,
                                        },
                                    }}
                                />
                            ),
                        }}
                        onOverflowOpenChange={(e, props) => {
                            setOverflowOpen(!!props?.overflowOpen);
                        }}
                        onOverflow={(items) => setOverflowItems(legendItems.length - items)}
                        getOverflowItems={(startIndex) => legendItems.slice(startIndex)}
                        styles={{
                            width: "100%",
                            backgroundColor: colorScheme.grey.background,
                        }}
                    />
                </Box>
            }
            
        </Flex>
    );
};
